import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DemoNavbar from "components/Navbars/DemoNavbar.js";
import StudioChat from "components/Studio/StudioChat";
import StudioFiles from "components/Studio/StudioFiles";
import StudioPreview from "components/Studio/StudioPreview";
import StudioPublish from "components/Studio/StudioPublish";
import StudioVersions from "components/Studio/StudioVersions";
import {
  capacityPausedText,
  errorText,
  formatTime,
  isTrialPlan,
  notEnoughText,
  outcomeNote,
  projectStatus,
} from "components/Studio/studioText";
import { useUserAuth } from "context/UserContext";
import useBreakpoint from "hooks/useBreakpoint";
import { isStudioConfigured, studioApi } from "services/studioService";
import "./studio.css";

/* /studio/:projectId. Chat on the left, the app on the right.

   Everything lives on the Studio service, not in this tab: closing the page
   mid-turn loses nothing (the turn finishes and is saved), and opening the
   project on another computer shows the same state. While something is still
   moving on the server (a turn running elsewhere, the app being set up, a
   preview deploying) the page polls the project every few seconds.

   The plan (me.plan, a PlanView) says what a message costs and what is left;
   each turn's "done" event carries the plan as it stands after the turn. */

const TABS = ["preview", "files", "versions", "publish"];
const POLL_MS = 4000;

// /studio/me lists models as { id, tier, uses, allowed }. An older service
// listed bare ids; read those as allowed Standard models.
const normalizeModels = (models) =>
  (Array.isArray(models) ? models : []).map((m) =>
    typeof m === "string" ? { id: m, tier: null, uses: 1, allowed: true } : m
  );

// What to do after a refusal, by code: where the answer is.
const SEE_PLANS = new Set(["plan_required", "trial_ended", "plan_lapsed", "project_limit", "live_app_limit"]);
const SEE_YOUR_PLAN = new Set(["out_of_messages", "fixes_used_up", "model_not_in_plan"]);

// The trial has no plan to look at in Account settings and nothing resets:
// its way out is a plan.
const TRIAL_SEE_PLANS = new Set(["out_of_messages", "capacity_paused"]);

// `plan` is the PlanView the page holds, if any: on the free trial resetsAt is
// the day the trial ends, so the wording never says "reset" there.
export function refusalNotice(t, err, lang, plan = null) {
  const details = err?.details || {};
  const trial = isTrialPlan(plan);
  let text = errorText(t, err);
  if (err?.code === "capacity_paused") {
    text = capacityPausedText(t, plan, details.resetsAt, lang);
  } else if (err?.code === "out_of_messages" && (details.resetsAt || trial)) {
    // Instead of the generic sentence, not after it: both say "not enough".
    text = notEnoughText(t, plan, details.resetsAt, lang);
  } else if ((err?.code === "trial_daily_limit" || err?.code === "slow_down") && details.retryAt) {
    text = `${text} ${t("studio.chat.trialRetry", { time: formatTime(details.retryAt, lang) })}`;
  }
  const seePlans = SEE_PLANS.has(err?.code) || (trial && TRIAL_SEE_PLANS.has(err?.code));
  const seeYourPlan = !seePlans && SEE_YOUR_PLAN.has(err?.code);
  return (
    <p>
      {text}{" "}
      {seePlans && <Link to="/studio/plans">{t("studio.chat.seePlans")}</Link>}
      {seeYourPlan && <Link to="/accountsettings?tab=billing">{t("studio.chat.seeYourPlan")}</Link>}
    </p>
  );
}

function StudioWorkspace() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { user } = useUserAuth();
  const { isDesktop } = useBreakpoint();

  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [quickPreviewPath, setQuickPreviewPath] = useState(null);
  const [messages, setMessages] = useState([]);
  const [me, setMe] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rulesFlag, setRulesFlag] = useState(false);
  const [tab, setTab] = useState(searchParams.get("hosting") ? "publish" : "preview");
  const [mobilePane, setMobilePane] = useState("chat");
  const sendingRef = useRef(false);

  const invited = user?.studioEnabled === true;
  const lang = i18n.language;

  const refreshProject = useCallback(async () => {
    const data = await studioApi.getProject(user, projectId);
    setProject(data.project);
    setFiles(data.files);
    setQuickPreviewPath(data.quickPreviewPath);
    return data.project;
  }, [user, projectId]);

  const refreshMessages = useCallback(async () => {
    const data = await studioApi.messages(user, projectId);
    setMessages(data.messages);
  }, [user, projectId]);

  const refreshMe = useCallback(() => studioApi.me(user).then(setMe).catch(() => {}), [user]);

  useEffect(() => {
    if (!isStudioConfigured || !invited) return;
    Promise.all([refreshProject(), refreshMessages(), refreshMe()]).catch((err) => setLoadError(errorText(t, err)));
  }, [invited, refreshProject, refreshMessages, refreshMe, t]);

  // Poll while the server is doing something this tab did not start.
  const settling =
    project &&
    !busy &&
    (project.activeTurn ||
      project.status === "provisioning" ||
      (project.status === "ready" && (project.previewVersion ?? 0) < project.currentVersion && !project.previewErrorCode));

  useEffect(() => {
    if (!settling) return undefined;
    const timer = setInterval(async () => {
      try {
        const wasRunning = Boolean(project?.activeTurn);
        const fresh = await refreshProject();
        if (wasRunning && !fresh.activeTurn) await refreshMessages();
      } catch {
        // next tick
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [settling, project, refreshProject, refreshMessages]);

  // One AI turn: a message, "continue" (picks up the last turn that hit its
  // work limit) or "fix" (Fix what broke). For the last two the server writes
  // the prompt.
  const runTurn = async ({ kind = "message", text = "" }) => {
    if (busy || sendingRef.current) return;
    sendingRef.current = true;
    setBusy(true);
    setNotice(null);
    setRulesFlag(false);
    setMessages((list) => [...list, { id: `local-${Date.now()}`, role: "user", kind, text }]);
    setLive({ text: "", tools: [] });
    if (!isDesktop) setMobilePane("chat");
    const body = kind === "message" ? { message: text, model: project?.model } : { model: project?.model, kind };
    try {
      await studioApi.sendMessage(user, projectId, body, (type, data) => {
        if (type === "text") setLive((l) => (l ? { ...l, text: l.text + (data?.delta ?? "") } : l));
        else if (type === "tool") setLive((l) => (l ? { ...l, tools: [...l.tools, data?.line ?? ""] } : l));
        else if (type === "version") {
          setQuickPreviewPath(data.quickPreviewPath);
          setProject((p) => (p ? { ...p, currentVersion: data.n } : p));
        } else if (type === "done") {
          // The plan as it stands after this turn.
          if (data?.plan) setMe((m) => (m ? { ...m, plan: { ...data.plan, liveUsed: data.plan.liveUsed ?? m.plan?.liveUsed } } : m));
          // A turn that crashed after it started has no stored reply to show,
          // so say what happened here.
          if (data?.outcome && data.outcome !== "done" && !data.assistantMessageId) {
            const trial = isTrialPlan(data.plan || me?.plan);
            setNotice(<p>{outcomeNote(t, data.outcome, data.errorCode, data, { trial }) || errorText(t, { code: "unknown", message: data.error })}</p>);
          }
        } else if (type === "rules") {
          setRulesFlag(true);
          setNotice(
            <>
              <p>{t("studio.chat.rulesWarning")}</p>
              <ul>{(data?.problems ?? []).map((line, i) => <li key={i}>{line}</li>)}</ul>
            </>
          );
        }
      });
    } catch (err) {
      setNotice(refusalNotice(t, err, lang, me?.plan));
      if (kind === "message") setInput(text);
    } finally {
      setLive(null);
      setBusy(false);
      sendingRef.current = false;
      await Promise.all([refreshMessages(), refreshProject(), refreshMe()]).catch(() => {});
    }
  };

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    runTurn({ kind: "message", text });
  };

  const stop = () => studioApi.stop(user, projectId).catch(() => {});

  const changeModel = async (model) => {
    try {
      const data = await studioApi.updateProject(user, projectId, { model });
      setProject(data.project);
    } catch (err) {
      setNotice(refusalNotice(t, err, lang, me?.plan));
    }
  };

  if (!isStudioConfigured || !invited || loadError) {
    return (
      <>
        <DemoNavbar />
        <main className="studio studio-home">
          <div className="container" style={{ paddingTop: 48 }}>
            <div className="studio-panel">
              <div className="studio-notice">
                <p>{loadError || (!isStudioConfigured ? t("studio.home.notConfigured") : t("studio.home.inviteOnly"))}</p>
              </div>
              <p style={{ marginTop: 16 }}><Link className="studio-link" to="/studio">{t("studio.workspace.backToProjects")}</Link></p>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (!project) {
    return (
      <>
        <DemoNavbar />
        <main className="studio studio-workspace"><p className="studio-muted studio-loading" style={{ padding: 24 }}>{t("studio.common.loading")}</p></main>
      </>
    );
  }

  const status = projectStatus(t, project);
  const turnElsewhere = Boolean(project.activeTurn) && !busy;
  const plan = me?.plan || null;
  const models = normalizeModels(me?.models);
  const model = models.find((m) => m.id === project.model) || null;
  const standard = models.find((m) => m.tier === "standard" && m.allowed !== false);
  const lastAi = [...messages].reverse().find((m) => m.role === "assistant");
  // Fix what broke is suggested when something visibly went wrong.
  const fixHighlight = Boolean(project.previewErrorCode) || lastAi?.outcome === "error" || rulesFlag;
  const modelLabel = (m) => {
    const label = m.tier ? t(`studio.models.${m.tier}`) : m.id;
    return m.allowed === false ? t("studio.models.businessOnly", { label }) : label;
  };

  const side = (
    <>
      <div className="studio-tabs" role="tablist">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`studio-tab ${tab === key ? "studio-tab-active" : ""}`}
            onClick={() => setTab(key)}
          >
            {t(`studio.tabs.${key}`)}
          </button>
        ))}
      </div>
      {tab === "preview" && <StudioPreview project={project} quickPreviewPath={quickPreviewPath} plan={plan} />}
      {tab === "files" && <StudioFiles projectId={projectId} files={files} version={project.currentVersion} />}
      {tab === "versions" && (
        <StudioVersions
          projectId={projectId}
          currentVersion={project.currentVersion}
          busy={busy || turnElsewhere}
          onRestored={() => refreshProject().catch(() => {})}
        />
      )}
      {tab === "publish" && (
        <StudioPublish
          project={project}
          plan={plan}
          onChanged={() => refreshProject().catch(() => {})}
          onPlanChanged={refreshMe}
        />
      )}
    </>
  );

  return (
    <>
      <DemoNavbar />
      <main className="studio studio-workspace">
        <header className="studio-bar">
          <Link to="/studio">{t("studio.workspace.backToProjects")}</Link>
          <h1 className="studio-bar__name">{project.name}</h1>
          <span className={`studio-chip studio-chip-dark`}>{status.label}</span>
          <span className="studio-bar__spacer" />
          {plan?.messages && (
            <Link
              className="studio-chip studio-chip-dark studio-chip-link"
              to="/accountsettings?tab=billing"
              aria-label={t("studio.plan.chipLabel", { left: plan.messages.left ?? 0 })}
            >
              {t("studio.plan.chip", { left: plan.messages.left ?? 0 })}
            </Link>
          )}
          {models.length > 1 && (
            <select
              className="studio-select"
              value={project.model}
              onChange={(e) => changeModel(e.target.value)}
              disabled={busy}
              aria-label={t("studio.workspace.model")}
            >
              {models.map((m) => (
                <option key={m.id} value={m.id} disabled={m.allowed === false && m.id !== project.model}>
                  {modelLabel(m)}
                </option>
              ))}
            </select>
          )}
        </header>

        {!isDesktop && (
          <div className="studio-mobile-switch">
            {["chat", "app"].map((pane) => (
              <button
                key={pane}
                type="button"
                className={`studio-btn studio-btn-small ${mobilePane === pane ? "studio-btn-primary" : "studio-btn-dark"}`}
                onClick={() => setMobilePane(pane)}
              >
                {t(`studio.workspace.pane.${pane}`)}
              </button>
            ))}
          </div>
        )}

        <div className="studio-panes">
          <section className={`studio-chat-pane ${!isDesktop && mobilePane !== "chat" ? "studio-pane-hidden" : ""}`} aria-label={t("studio.workspace.pane.chat")}>
            <StudioChat
              messages={messages}
              live={live || (turnElsewhere ? { text: "", tools: [t("studio.chat.runningElsewhere")] } : null)}
              busy={busy || turnElsewhere}
              input={input}
              setInput={setInput}
              onSend={send}
              onStop={stop}
              notice={notice}
              readOnly={busy || turnElsewhere}
              plan={plan}
              model={model}
              models={models}
              locale={lang}
              onContinue={() => runTurn({ kind: "continue" })}
              onFix={() => runTurn({ kind: "fix" })}
              onSwitchStandard={standard ? () => changeModel(standard.id) : undefined}
              fixHighlight={fixHighlight}
            />
          </section>
          <section className={`studio-side-pane ${!isDesktop && mobilePane !== "app" ? "studio-pane-hidden" : ""}`} aria-label={t("studio.workspace.pane.app")}>
            {side}
          </section>
        </div>
      </main>
    </>
  );
}

export default StudioWorkspace;
